import Img from "../img";
import Link from "next/link";
import VerifyBadge from "../verify-badge";
import shared from "../../data/js/shared";
import { useCategory } from "../../contexts";
import { dasherize, googleEvent, name, NumberFormat } from "../../utils/dom";
import { TRUST_LEVEL } from "../../data/js/trust-levels";
import { getConditionLabel } from "../../data/js/product-categories";

const {
  fullDescriptionHeading,
  prevButtonLabel,
  nextButtonLabel,
  postedByLabel,
  profilePhotoAlt,
} = shared.product;

const { conditionLabel } = shared.post;

export const CategoryBreadcrumb = ({ categoryId, categoryGroupId }: { categoryId: number | string, categoryGroupId?: number | string }) => {
  const { getCategoryById, categories } = useCategory();

  // Handle empty or invalid IDs
  if (!categoryId && !categoryGroupId) return null;

  const catId = typeof categoryId === 'string' ? parseInt(categoryId) : categoryId;
  const groupId = categoryGroupId ? (typeof categoryGroupId === 'string' ? parseInt(categoryGroupId) : categoryGroupId) : null;

  // Get the category and group category
  const category = catId ? getCategoryById(catId) : null;
  const groupCategory = groupId ? getCategoryById(groupId) : null;

  // If categories haven't loaded yet, return null
  if (categories.length === 0) return null;

  if (!category && !groupCategory) return null;

  // If we have both category and group, and they're different, show both
  // Both parent (groupCategory) and subcategory should be clickable
  if (category && groupCategory && groupCategory.id !== category.id) {
    const groupUrl = `/browse/${groupCategory.slug || dasherize(groupCategory.label)}/`;
    const categoryUrl = `/browse/${groupCategory.slug || dasherize(groupCategory.label)}/${category.slug || dasherize(category.label)}/`;
    return (
      <div className="">
        <a href={groupUrl}>{groupCategory.label}</a> · <a href={categoryUrl}>{category.label}</a>
      </div>
    );
  }

  // If we only have the category, show it
  if (category) {
    // Check if this category has a parent (is a subcategory)
    const parentCategory = category.parentId ? getCategoryById(category.parentId) : null;
    const url = parentCategory
      ? `/browse/${parentCategory.slug || dasherize(parentCategory.label)}/${category.slug || dasherize(category.label)}/`
      : `/browse/${category.slug || dasherize(category.label)}/`;
    return (
      <div className="">
        <a href={url}>{category.label}</a>
      </div>
    );
  }

  // Only the group category is left: the checks above returned otherwise.
  const url = `/browse/${groupCategory.slug || dasherize(groupCategory.label)}/`;
  return (
    <div className="">
      <a href={url}>{groupCategory.label}</a>
    </div>
  );
};

export const decodeHtmlEntities = (text: string): string => {
  // Decode common HTML entities
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'");
};

export const NewLineToBR = ({ text }: { text: string }) => {
  const decodedText = decodeHtmlEntities(text);
  const lines = decodedText.split("\n");
  return lines.map((line, index) => (
    <p key={`line-${line.slice(0, 20) || String(index)}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </p>
  ));
};

export const FlipButtons = ({ prevProduct, nextProduct }: { prevProduct: () => void; nextProduct: () => void }) => {
  return (
    <div className="product-flip-buttons">
      <a
        href="#"
        aria-label={prevButtonLabel}
        className="prev-product-button product-flip-button same-color-3"
        onClick={(e: React.MouseEvent) => { e.preventDefault(); prevProduct(); }}
      >
        <div className="prev-arrow">
        <Img className="dark-icon" src="/images/svg/back-arrow.svg" />
        <Img className="light-icon" src="/images/svg/back-arrow-dark.svg" />
        </div>
      </a>
      <a
        href="#"
        aria-label={nextButtonLabel}
        className="next-product-button product-flip-button same-color-3"
        onClick={(e: React.MouseEvent) => { e.preventDefault(); nextProduct(); }}
      >
        <div className="next-arrow">
          <Img className="dark-icon" src="/images/svg/back-arrow.svg" />
          <Img className="light-icon" src="/images/svg/back-arrow-dark.svg" />
        </div>
      </a>
    </div>
  );
};

export const VehicleInfo = ({ vin, mileage }: { vin?: string; mileage?: number }) => {
  return (
    <p className="vehicle-info">
      {vin ? <span className="vin">VIN: {vin}</span> : ""}&nbsp;&nbsp;
      {mileage > 0 ? (
        <span className="mileage">Mileage: {NumberFormat(mileage)}</span>
      ) : (
        ""
      )}
    </p>
  );
};

/**
 * The item's condition, on its own line above the dimensions.
 *
 * Buyers were reading condition out of whatever the seller happened to type into
 * the description, when it is a required field on every posted listing. Feed
 * imports and older records carry no condition id at all, so an unrecognised or
 * absent value renders nothing rather than an empty "Condition:" label.
 */
export const Condition = ({ itemCondition }: { itemCondition?: number | null }) => {
  const label = getConditionLabel(itemCondition);
  if (!label) return null;
  return (
    <p className="item-condition">
      <span className="condition">
        {conditionLabel}: {label}
      </span>
    </p>
  );
};

export const Dimensions = ({ height, width, depth, weight }: { height: number; width: number; depth: number; weight: number }) => {
  if (height > 0 && width > 0 && depth > 0 && weight > 0)
    return (
      <p>
        <span className="area">
          Dimensions: {height}&quot; x {width}&quot; x {depth}&quot; (in)
        </span>
        &nbsp;&nbsp;
        {weight != 1.0 ? (
          <span className="mass" data-shipping-weight={`${weight} lbs`}>Weight: {weight} (lbs)</span>
        ) : (
          ""
        )}
      </p>
    );
  else return "";
};

export const moreDescriptionButton = ({ setPopupContents, setPopupOpen, product }: { setPopupContents: (content: React.ReactNode) => void; setPopupOpen: (open: boolean) => void; product: Record<string, unknown> }) => {
  const description = product.text as string;
  googleEvent("more_description", product.id);
  setPopupContents(
    <div className="more-description">
      <h3>{fullDescriptionHeading}</h3>
      <div className="scrollable">
        <NewLineToBR text={description} />
      </div>
    </div>,
  );
  setPopupOpen(true);
};

export const UserViz = ({ user }: { user: Record<string, unknown> }) => {
  const userId = user?.id?.toString() || "";
  const lastDigit = userId.charAt(userId.length - 1) || "0";

  const userPhotos = user?.photos as
    | { thumbUrl?: string }
    | Array<{ thumbUrl?: string }>
    | undefined;
  let userPhoto = Array.isArray(userPhotos)
    ? userPhotos[0]?.thumbUrl
    : userPhotos?.thumbUrl;

  // If no photo or default photo, use placeholder
  if (!userPhoto || userPhoto.includes("default_t.jpg")) {
    userPhoto = `/images/svg/profile-placeholder-${lastDigit}.svg`;
  }

  const userName = name(user);

  return (
    <div className="user-viz">
      <div className="profile-pic">
        <img alt={profilePhotoAlt} src={userPhoto} />
      </div>
      <div className="user-name">
        <span>
          {postedByLabel}&nbsp;{userName}
        </span>
        <VerifyBadge user={user} tip={shared.product.verifiedTooltip} />
      </div>
    </div>
  );
};

export const SellerInfo = ({ user, slug: _slug }: { user: Record<string, unknown>; slug?: string }) => {
  const isWorthy = Number(user?.trustLevel) >= TRUST_LEVEL.VERIFIED;
  // Use username if available, otherwise fall back to ID
  const userSlug = user?.username || user?.id || "";
  const inside = <UserViz user={user} />;
  return isWorthy ? (
    <Link
      href={`/s/${userSlug}`}
      className="user-info-cont"
    >
      {inside}
    </Link>
  ) : (
    <div className="user-info-cont">{inside}</div>
  );
};
